import type {
  AdsState,
  MaterialUse,
  EventAccess,
  EventImageKind,
  EventSummary,
  Geofence,
  IngestStatus,
  Participant,
  ResultsConfigInput,
  ResultsProbe,
  ResultsStatus,
  RestreamStatus,
  RestreamTargetInput,
  StreamSource,
  TickerEntry,
  TickerState,
  WeatherState,
  ParticipantSocials,
} from '@rabbitfuel/shared';
import { MAX_CLIP_UPLOAD_BYTES, type Coords } from '@rabbitfuel/shared';
import { readToken } from './auth.tsx';
import { reportGateLocked } from './gate-signal.ts';

export interface EventTeam {
  adminEmail: string;
  deputyEmail: string | null;
  directors: string[];
}

export interface EventDetail extends EventSummary {
  participants: Participant[];
}

export interface EventDetailResponse {
  event: EventDetail;
  sources: StreamSource[];
  activeSourceId: string | null;
  overlaySourceId: string | null;
  ticker: TickerState;
  tickerFeed: TickerEntry[];
}

/**
 * Die Routen antworten bei fehlender Berechtigung mit Maschinencodes. Die
 * gehoeren nicht ungefiltert in die Oberflaeche: ein nacktes "forbidden" im
 * Restream-Panel liest sich, als haette YouTube oder Twitch den
 * Streamschluessel abgelehnt -- dabei ist es der eigene Server, dem die
 * Regie-Anmeldung fehlt.
 */
const MACHINE_ERRORS: Record<string, string> = {
  forbidden:
    'Keine Regie-Berechtigung. Mit dem Veranstalter-Konto anmelden oder beim Beitritt die Veranstalter-PIN eingeben.',
  not_found: 'Dieses Event gibt es nicht (mehr).',
};

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const token = readToken();
  const res = await fetch(path, {
    ...init,
    headers: {
      /*
       * Nur mit Inhalt. Ein DELETE ohne Inhalt, aber mit diesem Kopf lehnt
       * Fastify mit 400 ab („Body cannot be empty when content-type is set
       * to 'application/json'") -- so liess sich der laufende Stream nicht
       * beenden und kein Eintrag aus der Startliste loeschen.
       */
      ...(init?.body !== undefined ? { 'content-type': 'application/json' } : {}),
      // Angemeldete Konten sind der regulaere Weg; die PIN kommt als Header
      // nur dort dazu, wo sie als Notfall-Zugang gebraucht wird.
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    reportGateLocked(res.status, body);
    const code = (body as { error?: string }).error;
    if (code) throw new Error(MACHINE_ERRORS[code] ?? code);
    throw new Error(`HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

const pinHeader = (pin: string) => ({ 'x-organizer-pin': pin });

export const api = {
  listEvents: () => req<{ events: EventSummary[] }>('/api/events').then((r) => r.events),

  getEvent: (slug: string) => req<EventDetailResponse>(`/api/events/${slug}`),

  /** Nur der Ticker -- die Event-Seite pollt das, ohne alles neu zu laden. */
  getTicker: (slug: string) =>
    req<{ ticker: TickerState; feed: TickerEntry[] }>(`/api/events/${slug}/ticker`),

  createEvent: (body: {
    title: string;
    club: string;
    sport?: string;
    discipline?: string;
    description?: string;
    location?: string;
    country?: string;
    language?: string;
    startsAt?: string;
    organizerPin: string;
    deputyEmail?: string | null;
    cover?: string | null;
    logo?: string | null;
  }) => req<{ event: EventSummary }>('/api/events', { method: 'POST', body: JSON.stringify(body) }),

  updateEvent: (
    slug: string,
    pin: string | undefined,
    body: {
      status?: 'upcoming' | 'live' | 'finished';
      title?: string;
      club?: string;
      sport?: string;
      discipline?: string;
      description?: string;
      location?: string;
      country?: string;
      language?: string;
      startsAt?: string;
    },
  ) =>
    req<{ event: EventSummary }>(`/api/events/${slug}`, {
      method: 'PATCH',
      headers: pin ? pinHeader(pin) : {},
      body: JSON.stringify(body),
    }),

  /** cover/logo/backdrop als data:-URL; null loescht das Bild. */
  updateImages: (
    slug: string,
    pin: string | undefined,
    body: Partial<Record<EventImageKind, string | null>>,
  ) =>
    req<{ event: EventSummary }>(`/api/events/${slug}/images`, {
      method: 'PUT',
      headers: pin ? pinHeader(pin) : {},
      body: JSON.stringify(body),
    }),

  addParticipant: (slug: string, pin: string | undefined, body: { bib: string; name: string; team?: string; category?: string }) =>
    req<{ participants: Participant[] }>(`/api/events/${slug}/participants`, {
      method: 'POST',
      headers: pin ? pinHeader(pin) : {},
      body: JSON.stringify(body),
    }),

  bulkParticipants: (slug: string, pin: string | undefined, text: string) =>
    req<{ added: number; skipped: string[]; participants: Participant[] }>(
      `/api/events/${slug}/participants/bulk`,
      { method: 'POST', headers: pin ? pinHeader(pin) : {}, body: JSON.stringify({ text }) },
    ),

  /** Angaben fuers Athleten-Pop-up -- `photo` als data:-URL, null loescht den Headshot. */
  updateParticipant: (
    slug: string,
    pin: string | undefined,
    participantId: string,
    body: { name?: string; team?: string; info?: string; socials?: ParticipantSocials; photo?: string | null },
  ) =>
    req<{ participant: Participant; participants: Participant[] }>(`/api/events/${slug}/participants/${participantId}`, {
      method: 'PATCH',
      headers: pin ? pinHeader(pin) : {},
      body: JSON.stringify(body),
    }),

  removeParticipant: (slug: string, pin: string | undefined, participantId: string) =>
    req<{ participants: Participant[] }>(`/api/events/${slug}/participants/${participantId}`, {
      method: 'DELETE',
      headers: pin ? pinHeader(pin) : {},
    }),

  lookupBib: (slug: string, bib: string) =>
    req<{ found: boolean; participant: Participant | null }>(
      `/api/events/${slug}/bib?bib=${encodeURIComponent(bib)}`,
    ),

  access: (slug: string) =>
    req<{ access: EventAccess; team: EventTeam | null }>(`/api/events/${slug}/access`),

  updateTeam: (slug: string, body: { directors?: string[]; deputyEmail?: string | null }) =>
    req<{ team: EventTeam }>(`/api/events/${slug}/team`, { method: 'PUT', body: JSON.stringify(body) }),

  updateGeofence: (slug: string, pin: string | undefined, geofence: Geofence | null) =>
    req<{ geofence: Geofence | null }>(`/api/events/${slug}/geofence`, {
      method: 'PUT',
      headers: pin ? pinHeader(pin) : {},
      body: JSON.stringify({ geofence }),
    }),

  /* ------------------------------------------ Ausgang zu YouTube/Twitch */

  getRestream: (slug: string, pin: string | undefined) =>
    req<{ status: RestreamStatus; enabled: boolean }>(`/api/events/${slug}/restream`, {
      headers: pin ? pinHeader(pin) : {},
    }),

  /**
   * Die Streamschluessel gehen genau hier einmal hoch und kommen nie zurueck.
   * Deshalb REST und nicht die Event-WebSocket, die auch Chat und Ticker traegt.
   */
  startRestream: (slug: string, pin: string | undefined, targets: RestreamTargetInput[]) =>
    req<{ status: RestreamStatus }>(`/api/events/${slug}/restream`, {
      method: 'POST',
      headers: pin ? pinHeader(pin) : {},
      body: JSON.stringify({ targets }),
    }),

  stopRestream: (slug: string, pin: string | undefined) =>
    req<{ status: RestreamStatus }>(`/api/events/${slug}/restream`, {
      method: 'DELETE',
      headers: pin ? pinHeader(pin) : {},
    }),

  /* ------------------------------------------------------ RTMP-Eingang */

  /** Mit Schluesseln -- deshalb nur fuer die Regie und nie ueber die WebSocket. */
  getIngest: (slug: string, pin: string | undefined) =>
    req<IngestStatus>(`/api/events/${slug}/ingest`, { headers: pin ? pinHeader(pin) : {} }),

  addIngest: (slug: string, pin: string | undefined, name: string) =>
    req<IngestStatus>(`/api/events/${slug}/ingest`, {
      method: 'POST',
      headers: pin ? pinHeader(pin) : {},
      body: JSON.stringify({ name }),
    }),

  updateIngest: (slug: string, pin: string | undefined, id: string, patch: { name?: string; renewKey?: boolean }) =>
    req<IngestStatus>(`/api/events/${slug}/ingest/${id}`, {
      method: 'PATCH',
      headers: pin ? pinHeader(pin) : {},
      body: JSON.stringify(patch),
    }),

  removeIngest: (slug: string, pin: string | undefined, id: string) =>
    req<IngestStatus>(`/api/events/${slug}/ingest/${id}`, {
      method: 'DELETE',
      headers: pin ? pinHeader(pin) : {},
    }),

  /* --------------------------------------------------------------- Wetter */

  getWeather: (slug: string) => req<{ weather: WeatherState }>(`/api/events/${slug}/weather`),

  /* ------------------------------------------- Ergebnisse aus RACE RESULT */

  getResults: (slug: string, pin: string | undefined) =>
    req<{ status: ResultsStatus; enabled: boolean }>(`/api/events/${slug}/results`, {
      headers: pin ? pinHeader(pin) : {},
    }),

  /**
   * Der API-Key geht hier einmal hoch und kommt nie zurueck -- aus demselben
   * Grund wie der Streamschluessel per REST statt ueber die Event-WebSocket.
   */
  saveResults: (slug: string, pin: string | undefined, config: ResultsConfigInput) =>
    req<{ status: ResultsStatus }>(`/api/events/${slug}/results`, {
      method: 'PUT',
      headers: pin ? pinHeader(pin) : {},
      body: JSON.stringify(config),
    }),

  /** Einmal abfragen, ohne etwas zu uebernehmen. */
  testResults: (slug: string, pin: string | undefined, config: ResultsConfigInput) =>
    req<{ probe: ResultsProbe }>(`/api/events/${slug}/results/test`, {
      method: 'POST',
      headers: pin ? pinHeader(pin) : {},
      body: JSON.stringify(config),
    }),

  /* --------------------------------------------------------- Werbung */

  /** `logo` als data:-URL -- wird Material mit „Logo im Bild" und „Banner". Antwortet mit dem neuen Stand fuer alle. */
  createSponsor: (slug: string, pin: string | undefined, body: { name: string; logo?: string | null }) =>
    req<{ ads: AdsState }>(`/api/events/${slug}/sponsors`, {
      method: 'POST',
      headers: pin ? pinHeader(pin) : {},
      body: JSON.stringify(body),
    }),

  /** Bild als Werbematerial, `image` als data:-URL. */
  addSponsorImage: (
    slug: string,
    pin: string | undefined,
    id: string,
    body: { image: string; label: string; uses?: MaterialUse[] },
  ) =>
    req<{ ads: AdsState }>(`/api/events/${slug}/sponsors/${id}/materials`, {
      method: 'POST',
      headers: pin ? pinHeader(pin) : {},
      body: JSON.stringify(body),
    }),

  deleteSponsor: (slug: string, pin: string | undefined, id: string) =>
    req<{ ads: AdsState }>(`/api/events/${slug}/sponsors/${id}`, {
      method: 'DELETE',
      headers: pin ? pinHeader(pin) : {},
    }),

  deleteMaterial: (slug: string, pin: string | undefined, id: string, materialId: string) =>
    req<{ ads: AdsState }>(`/api/events/${slug}/sponsors/${id}/materials/${materialId}`, {
      method: 'DELETE',
      headers: pin ? pinHeader(pin) : {},
    }),

  /**
   * Video als Werbematerial hochladen. Ueber XHR statt fetch, weil nur XHR
   * den Fortschritt des *Hochladens* meldet -- bei 80 MB ueber ein
   * Hallen-WLAN will die Regie sehen, dass etwas passiert. Danach wandelt der
   * Server um; `onProgress(1)` heisst also „angekommen", nicht „fertig".
   */
  uploadSponsorVideo: (
    slug: string,
    pin: string | undefined,
    id: string,
    file: File,
    durationMs: number | null,
    uses: MaterialUse[],
    onProgress: (share: number) => void,
  ) =>
    new Promise<{ ads: AdsState }>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', `/api/events/${slug}/sponsors/${id}/materials`);
      // Manche Browser kennen den Typ einer .mkv nicht -- umgewandelt wird ohnehin.
      xhr.setRequestHeader('content-type', file.type.startsWith('video/') ? file.type : 'video/mp4');
      if (durationMs) xhr.setRequestHeader('x-spot-duration', String(Math.round(durationMs)));
      // Kopfzeilen sind ASCII -- Umlaute im Dateinamen gehen kodiert.
      xhr.setRequestHeader('x-material-label', encodeURIComponent(file.name.slice(0, 80)));
      if (uses.length) xhr.setRequestHeader('x-material-uses', uses.join(','));
      const token = readToken();
      if (token) xhr.setRequestHeader('authorization', `Bearer ${token}`);
      if (pin) xhr.setRequestHeader('x-organizer-pin', pin);
      xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
      xhr.upload.onload = () => onProgress(1);
      xhr.onerror = () => reject(new Error('Upload abgebrochen — Verbindung zum Server weg?'));
      xhr.onload = () => {
        let body: unknown = null;
        try {
          body = JSON.parse(xhr.responseText);
        } catch {
          /* kein JSON -- dann zaehlt der Status */
        }
        if (xhr.status >= 200 && xhr.status < 300) return resolve(body as { ads: AdsState });
        reportGateLocked(xhr.status, body);
        if (xhr.status === 413) return reject(new Error('Die Datei ist zu groß (höchstens 200 MB).'));
        const code = (body as { error?: string } | null)?.error;
        reject(new Error(code ? (MACHINE_ERRORS[code] ?? code) : `HTTP ${xhr.status}`));
      };
      xhr.send(file);
    }),

  /**
   * Botschaft an die Regie hochladen -- auch ueber XHR, wegen des Fortschritts.
   * Wer schickt, sagt die connectionId: der Server liest daraus Name, Rolle
   * und Grenze dieser Sitzung. `onProgress(1)` heisst „angekommen"; danach
   * bereitet der Server sie auf, den Stand meldet er ueber die WebSocket.
   */
  /**
   * Darf diese Sitzung gerade eine Botschaft schicken? Vor der Aufnahme
   * gefragt -- bei „Nur vor Ort" mit Standort. Wirft mit dem Grund.
   */
  checkClip: async (slug: string, connectionId: string, location: Coords | null) => {
    const res = await fetch(`/api/events/${slug}/clips/check`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', 'x-connection-id': connectionId },
      body: JSON.stringify(location ?? {}),
    });
    if (res.ok) return;
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    reportGateLocked(res.status, body);
    throw new Error(body?.error ?? `HTTP ${res.status}`);
  },

  uploadClip: (
    slug: string,
    connectionId: string,
    blob: Blob,
    durationMs: number | null,
    location: Coords | null,
    onProgress: (share: number) => void,
  ) =>
    new Promise<{ id: string }>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `/api/events/${slug}/clips`);
      // Ohne Typ (manche Galerien) gilt es als Video -- der Server sieht ohnehin selbst nach.
      xhr.setRequestHeader('content-type', /^(video|audio)\//.test(blob.type) ? blob.type : 'video/mp4');
      xhr.setRequestHeader('x-connection-id', connectionId);
      xhr.setRequestHeader('x-clip-consent', 'yes');
      if (durationMs) xhr.setRequestHeader('x-clip-duration', String(Math.round(durationMs)));
      // Nur bei „Nur vor Ort" gesetzt: der Server prueft ihn noch einmal und speichert ihn nicht.
      if (location) {
        xhr.setRequestHeader('x-clip-lat', String(location.lat));
        xhr.setRequestHeader('x-clip-lng', String(location.lng));
        if (location.accuracy != null) xhr.setRequestHeader('x-clip-accuracy', String(Math.round(location.accuracy)));
      }
      xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
      xhr.upload.onload = () => onProgress(1);
      xhr.onerror = () => reject(new Error('Upload abgebrochen — ist die Verbindung weg? Versuch es gleich noch einmal.'));
      xhr.onload = () => {
        let body: unknown = null;
        try {
          body = JSON.parse(xhr.responseText);
        } catch {
          /* kein JSON -- dann zaehlt der Status */
        }
        if (xhr.status >= 200 && xhr.status < 300) return resolve(body as { id: string });
        reportGateLocked(xhr.status, body);
        if (xhr.status === 413) {
          return reject(new Error(`Die Datei ist zu groß (höchstens ${MAX_CLIP_UPLOAD_BYTES / 1024 / 1024} MB).`));
        }
        const message = (body as { error?: string } | null)?.error;
        reject(new Error(message ? (MACHINE_ERRORS[message] ?? message) : `HTTP ${xhr.status}`));
      };
      xhr.send(blob);
    }),

  /** Nur im POC: PIN anzeigen, damit man ohne Absprache testen kann. */
  demoPin: (slug: string) => req<{ pin: string }>(`/api/events/${slug}/demo-pin`).then((r) => r.pin),
};
